/**
 * IndexedDB-backed small-image cache. Stores the raw blob bytes for each
 * resolved thumbnail/avatar so they survive page reloads without hitting the
 * network again (and remain available offline).
 *
 * Key schemas:
 *   `${uploadId}:${version}` — media thumbnails; version comes from the
 *     MediaUpload row, so a file edit that bumps `version` automatically
 *     misses the old entry (no explicit eviction needed for stale data).
 *   `avatar:${url}`          — avatar images keyed by their resolved URL.
 *
 * Eviction: capped at MAX_ENTRIES by timestamp (oldest-first) on each write.
 * Wiped on manual logout (wipeOfflineCaches deletes the whole DB).
 *
 * "Can't be thumbnailed" markers live in their own store (FAILURE_STORE): in
 * the shared one, a Library with more thumbnails than MAX_ENTRIES evicted them
 * within a session, so every reload re-downloaded the same undecryptable files.
 */

const DB_NAME = 'sleepy-thumbs';
// 2: FAILURE_STORE.
const DB_VERSION = 2;
const STORE = 'thumbnails';
const FAILURE_STORE = 'failures';
// Thumbnails (4–8 KB each) and avatars share the store: a big Library's
// thumbnails all stay (400 was evicting a folder's own while it was listed).
const MAX_ENTRIES = 4000;
// Failure markers are a key and a time each (the oldest go first past this).
const MAX_FAILURE_ENTRIES = 5000;

interface ThumbEntry {
  key: string;
  data: ArrayBuffer;
  mime: string;
  ts: number;
}

let _db: IDBDatabase | null = null;
let _opening: Promise<IDBDatabase> | null = null;
// An open the browser holds back (another tab keeps an older version of the DB
// open, so the upgrade waits for it) fails after this long instead of leaving
// every thumbnail lookup waiting on it: the caches are best-effort.
const OPEN_TIMEOUT_MS = 3000;

/** Close the shared connection so a security wipe can delete the database. */
export function closeThumbDb(): void {
  try { _db?.close(); } catch { /* already closed */ }
  _db = null;
}

function openDb(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  if (_opening) return _opening;
  _opening = new Promise<IDBDatabase>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('thumbnail cache open timed out')), OPEN_TIMEOUT_MS);
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      for (const name of [STORE, FAILURE_STORE]) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: 'key' }).createIndex('ts', 'ts');
      }
    };
    req.onsuccess = (e) => {
      clearTimeout(timer);
      const db = (e.target as IDBOpenDBRequest).result;
      // A newer tab upgrading the DB: let it (this one reopens on its next use).
      db.onversionchange = () => { db.close(); if (_db === db) _db = null; };
      _db = db;
      resolve(db);
    };
    req.onerror = () => { clearTimeout(timer); reject(req.error); };
  }).finally(() => { _opening = null; });
  return _opening;
}

function idbGet(db: IDBDatabase, key: string, store = STORE): Promise<ThumbEntry | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as ThumbEntry | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, entry: ThumbEntry, store = STORE): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbEvict(db: IDBDatabase, storeName = STORE, maxEntries = MAX_ENTRIES): Promise<void> {
  return new Promise((resolve) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const countReq = store.count();
    countReq.onsuccess = () => {
      const count = countReq.result;
      if (count <= maxEntries) { resolve(); return; }
      const toDelete = count - maxEntries;
      const curReq = store.index('ts').openCursor();
      let deleted = 0;
      curReq.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
        if (!cursor || deleted >= toDelete) { resolve(); return; }
        cursor.delete();
        deleted++;
        cursor.continue();
      };
      curReq.onerror = () => resolve();
    };
    countReq.onerror = () => resolve();
  });
}

/**
 * Returns a fresh blob URL for any cached entry, or null if not cached.
 * Caller owns the URL and must revoke it when done.
 */
export async function getIdbBlobUrl(key: string): Promise<string | null> {
  try {
    const db = await openDb();
    const entry = await idbGet(db, key);
    if (!entry || entry.mime === FAILED_MIME) return null;
    return URL.createObjectURL(new Blob([entry.data], { type: entry.mime }));
  } catch {
    return null;
  }
}

/**
 * Returns the raw cached bytes + mime for a key, or null. Use when the caller
 * needs the data itself (base64 conversion) rather than a renderable URL.
 */
export async function getIdbBlobBytes(key: string): Promise<{ data: ArrayBuffer; mime: string } | null> {
  try {
    const db = await openDb();
    const entry = await idbGet(db, key);
    if (!entry || entry.mime === FAILED_MIME) return null;
    return { data: entry.data, mime: entry.mime };
  } catch {
    return null;
  }
}

/**
 * Persist raw blob bytes under an arbitrary key (e.g. `avatar:<url>`).
 * Fire-and-forget — never throws or blocks the caller.
 */
export function storeIdbBlob(key: string, blob: Blob): void {
  (async () => {
    try {
      const data = await blob.arrayBuffer();
      const db = await openDb();
      await idbPut(db, { key, data, mime: blob.type || 'application/octet-stream', ts: Date.now() });
      await idbEvict(db);
    } catch { /* best-effort */ }
  })();
}

/**
 * Returns a fresh blob URL from the IDB cache, or null if not cached.
 * Caller owns the URL and must revoke it when done.
 */
export function getIdbThumb(id: string, version: number): Promise<string | null> {
  return getIdbBlobUrl(`${id}:${version}`);
}

/**
 * Persist a resolved blob URL into IDB so future sessions avoid re-fetching.
 * Fire-and-forget — never throws or blocks the caller.
 */
export function storeIdbThumb(id: string, version: number, blobUrl: string): void {
  (async () => {
    try {
      const res = await fetch(blobUrl);
      storeIdbBlob(`${id}:${version}`, await res.blob());
    } catch { /* best-effort */ }
  })();
}

// ── Persistent "can't be thumbnailed" marker ────────────────────────────────
// Lets a file whose thumbnail can't be made (a decrypt that fails its integrity
// check, a video no decoder reads, an .ai that isn't PDF-compatible, a file the
// server refuses) show its type icon and never be tried again, instead of paying
// the download every session. Keyed by id+version, so a re-upload or edit
// (version bump) gets one fresh attempt.
const FAILED_MIME = 'x-thumb/failed';

const failureKey = (id: string, version: number) => `${id}:${version}`;

export async function isIdbThumbFailed(id: string, version: number): Promise<boolean> {
  try {
    const db = await openDb();
    const entry = await idbGet(db, failureKey(id, version), FAILURE_STORE);
    return entry?.mime === FAILED_MIME;
  } catch {
    return false;
  }
}

export function markIdbThumbFailed(id: string, version: number): void {
  (async () => {
    try {
      const db = await openDb();
      await idbPut(db, { key: failureKey(id, version), data: new ArrayBuffer(0), mime: FAILED_MIME, ts: Date.now() }, FAILURE_STORE);
      await idbEvict(db, FAILURE_STORE, MAX_FAILURE_ENTRIES);
    } catch { /* best-effort */ }
  })();
}
